import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import { monospaceFontFamily } from "src/providers/theme";
import { makeStyles } from "tss-react/mui";

import { Dialog, DialogContent, Theme } from "@mui/material";
import { CompactIconButton } from "@shared/CustomButton";
import { LoadingIcon } from "@shared/Loading";

const useStyles = makeStyles()((theme: Theme) => ({
    dialogContent: {
        height: "calc(80vh - 24px)",
        background: "black",
        color: "white",
        overflow: "auto",
        padding: theme.spacing(5),
        flexGrow: 1,
    },
    modalTitleBox: {
        display: "flex",
        justifyContent: "space-between",
        color: "white",
        backgroundColor: "black",
        padding: theme.spacing(5),
    },
    modalBg: {
        backgroundColor: "black",
        color: "white",
    },
    titleLeftContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing(5),
    },
    logs: {
        color: "white",
        overflow: "visible",
        fontFamily: monospaceFontFamily,
    },
    contentContainer: {
        padding: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
    },
    statusIndicator: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    statusDot: {
        width: 8,
        height: 8,
        borderRadius: "50%",
    },
    statusConnected: {
        backgroundColor: theme.palette.emteria.success,
    },
    statusDisconnected: {
        backgroundColor: theme.palette.emteria.text.secondary,
    },
}));

type StreamStatusIndicatorProps = {
    isStreamActive?: boolean;
};

const StreamStatusIndicator = (props: StreamStatusIndicatorProps) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["modals", "common"]);

    if (props.isStreamActive === undefined) {
        return null;
    }

    const statusText = props.isStreamActive ? t("common:status.connected") : t("common:status.disconnected");

    return (
        <div className={classes.statusIndicator}>
            <div
                className={clsx(classes.statusDot, props.isStreamActive ? classes.statusConnected : classes.statusDisconnected)}
            />
            <span>{statusText}</span>
        </div>
    );
};

export type StreamStatus = "initializing" | "streaming" | "connected" | "done" | null;

type Props = {
    status: StreamStatus;
    downloadDisabled?: boolean;
    downloading?: boolean;
    onDownload?: () => void;
    onCopyLink?: () => void;
    title: string;
    open: boolean;
    onClose: () => void;
    containerRef: React.RefObject<HTMLDivElement | null>;
    contentRef: React.RefObject<HTMLPreElement | null>;
    hasContent: boolean;
};

export const LogStreamModal = (props: Props) => {
    const { containerRef, contentRef } = props;
    const { classes } = useStyles();
    const { t } = useTranslation(["modals", "common"]);

    const isStreamActive = props.status === "connected" || props.status === "streaming";

    return (
        <Dialog open={props.open} fullScreen disableEscapeKeyDown>
            <DialogContent className={classes.contentContainer}>
                <div className={classes.modalTitleBox}>
                    <div className={classes.titleLeftContainer}>
                        <div className={classes.modalBg}>{props.title}</div>
                        <StreamStatusIndicator isStreamActive={isStreamActive} />
                        {props.onDownload ? (
                            <CompactIconButton
                                disabled={props.status === "streaming" || props.downloadDisabled}
                                loading={props.downloading}
                                onClick={props.onDownload}
                                tooltipProps={{ title: t("modals:logStreamer.downloadLogs") }}
                            >
                                <i className="fa-solid fa-download" />
                            </CompactIconButton>
                        ) : null}
                        {props.onCopyLink ? (
                            <CompactIconButton
                                onClick={props.onCopyLink}
                                tooltipProps={{ title: t("modals:logStreamer.copyLink") }}
                            >
                                <i className="fa-solid fa-link" />
                            </CompactIconButton>
                        ) : null}
                    </div>
                    <CompactIconButton onClick={props.onClose} tooltipProps={{ title: t("common:button.close") }}>
                        <i className="fa-solid fa-xmark" />
                    </CompactIconButton>
                </div>
                <div ref={containerRef} className={classes.dialogContent}>
                    <pre className={classes.logs} ref={contentRef} />

                    {props.status === "initializing" ? <LoadingIcon size="block" inverse /> : null}

                    {props.status === "connected" ? <div>{t("modals:logStreamer.waitingForLogs")}</div> : null}

                    {props.status === "done" && !props.hasContent ? <div>{t("modals:logStreamer.noLogs")}</div> : null}
                </div>
            </DialogContent>
        </Dialog>
    );
};
